import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextAccounting } from '../Accounting';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { OpeningBalanceDA } from '../../../../services/admin/opening.balance.service';
import { AccountingAccountDA } from '../../../../services/admin/accounting.account.service';
import { ExchangeRateDA } from '../../../../services/admin/exchange.rate.service';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { AccountingAccount } from '../../../../models/accounting.account.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';

import AppPreloader from '../../../../components/Preloader';

export interface OpeningBalancePageProps {};

let errorResponse: Error;

interface BalanceLine {
  id_account: string;
  code: string;
  name: string;
  currency_code: string;
  debit: string;
  credit: string;
  amount_original: string;
  exchange_rate: string;
}

interface FiscalYear {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
}

function accountName(account: AccountingAccount, locale: string): string {
  const n = account.name as any
  return n?.[locale] ?? n?.fr ?? n?.en ?? n?.de ?? n?.it ?? account.code
}

function flattenLeaves(accounts: AccountingAccount[], locale: string): Array<{ id: string; code: string; name: string; currency_code: string }> {
  const result: Array<{ id: string; code: string; name: string; currency_code: string }> = []
  const walk = (list: AccountingAccount[]) => {
    for (const a of list) {
      if (a.type === 'leave' && /^[12]/.test(a.code)) {
        result.push({ id: a.id, code: a.code, name: accountName(a, locale), currency_code: a.currency?.code ?? 'CHF' })
      }
      if (a.children && a.children.length > 0) {
        walk(a.children)
      }
    }
  }
  walk(accounts)
  result.sort((a, b) => a.code.localeCompare(b.code))
  return result
}

const OpeningBalancePage: React.FunctionComponent<OpeningBalancePageProps> = () => {
  const {setRoute, companyForUser} = UseOutletContextAccounting()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const { id_fiscal_year } = useParams<{ id_fiscal_year: string }>()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [mounted, setMounted] = useState(false)
  const [fiscalYear, setFiscalYear] = useState<FiscalYear | null>(null)
  const [lines, setLines] = useState<BalanceLine[]>([])
  const [allAccounts, setAllAccounts] = useState<Array<{ id: string; code: string; name: string; currency_code: string }>>([])
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<'manual' | 'import'>('manual')
  const [saving, setSaving] = useState(false)
  const [importing, setImporting] = useState(false)
  const [hasExisting, setHasExisting] = useState(false)

  const isClosed = fiscalYear?.status === 'closed'
  const locale = lang.code?.substring(0, 2) ?? 'fr'

  const blockNegativeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '-') e.preventDefault()
  }

  const loadFiscalYear = async (id_company: string) => {
    try {
      const response = await FiscalYearDA.getFiscalYear(id_company, id_fiscal_year ?? '')
      if (response.status === 200 && response.data) {
        const fy = response.data
        setFiscalYear({
          id: fy.id ?? fy._id ?? '',
          name: fy.name ?? '',
          start_date: fy.start_date ?? '',
          end_date: fy.end_date ?? '',
          status: fy.status ?? '',
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const loadAccounts = async (id_company: string) => {
    try {
      const response = await AccountingAccountDA.getAccountingAccounts(id_company, [], ['1', '2'], [])
      if (response.status === 200) {
        const raw = response.data?.accounting_accounts ?? (Array.isArray(response.data) ? response.data : [])
        const leaves = flattenLeaves(raw, locale)
        setAllAccounts(leaves)
        return leaves
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
    return []
  }

  const loadExistingBalance = async (id_company: string, accounts: Array<{ id: string; code: string; name: string; currency_code: string }>) => {
    try {
      const response = await OpeningBalanceDA.getOpeningBalance(id_company, id_fiscal_year ?? '')
      if (response.status === 200 && response.data) {
        const data = response.data

        // Normaliser : le backend renvoie debits[]/credits[] avec `amount`.
        // Construire une map id_account → { debit, credit, amount_original, exchange_rate }.
        const balanceMap = new Map<string, { debit: number; credit: number; amount_original?: number; exchange_rate?: number }>()

        for (const d of (data.debits ?? [])) {
          const prev = balanceMap.get(d.id_account) ?? { debit: 0, credit: 0 }
          prev.debit += Number(d.amount) || 0
          if (d.amount_original != null) prev.amount_original = d.amount_original
          if (d.exchange_rate != null) prev.exchange_rate = d.exchange_rate
          balanceMap.set(d.id_account, prev)
        }
        for (const c of (data.credits ?? [])) {
          const prev = balanceMap.get(c.id_account) ?? { debit: 0, credit: 0 }
          prev.credit += Number(c.amount) || 0
          if (c.amount_original != null) prev.amount_original = c.amount_original
          if (c.exchange_rate != null) prev.exchange_rate = c.exchange_rate
          balanceMap.set(c.id_account, prev)
        }

        if (balanceMap.size > 0) {
          setHasExisting(true)
          const filledLines: BalanceLine[] = accounts.map(a => {
            const existing = balanceMap.get(a.id)
            return {
              id_account: a.id,
              code: a.code,
              name: a.name,
              currency_code: a.currency_code,
              debit: existing && existing.debit > 0 ? String(existing.debit) : '',
              credit: existing && existing.credit > 0 ? String(existing.credit) : '',
              amount_original: existing?.amount_original != null ? String(existing.amount_original) : '',
              exchange_rate: existing?.exchange_rate != null ? String(existing.exchange_rate) : '',
            }
          })
          setLines(filledLines)
          return
        }
      }
    } catch (error) {
      // 404 = no existing balance, which is fine
      if ((error as any)?.response?.status !== 404) {
        errorWebHandler(lang, error)
      }
    }
    // No existing balance: initialize empty lines
    setHasExisting(false)
    setLines(accounts.map(a => ({ id_account: a.id, code: a.code, name: a.name, currency_code: a.currency_code, debit: '', credit: '', amount_original: '', exchange_rate: '' })))
  }

  const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0)
  const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0)
  const diff = Math.round((totalDebit - totalCredit) * 100) / 100
  const isBalanced = diff === 0 && (totalDebit > 0 || totalCredit > 0)

  const computeChf = (amountOriginal: string, exchangeRate: string): string => {
    const orig = parseFloat(amountOriginal)
    const rate = parseFloat(exchangeRate)
    if (!isNaN(orig) && !isNaN(rate) && rate > 0) {
      return (orig / rate).toFixed(2)
    }
    return ''
  }

  const handleDebitChange = (index: number, value: string) => {
    const sanitized = value.includes('-') ? String(Math.abs(parseFloat(value)) || '') : value
    const updated = [...lines]
    updated[index] = { ...updated[index], debit: sanitized, credit: sanitized && parseFloat(sanitized) > 0 ? '' : updated[index].credit }
    setLines(updated)
  }

  const handleCreditChange = (index: number, value: string) => {
    const sanitized = value.includes('-') ? String(Math.abs(parseFloat(value)) || '') : value
    const updated = [...lines]
    updated[index] = { ...updated[index], credit: sanitized, debit: sanitized && parseFloat(sanitized) > 0 ? '' : updated[index].debit }
    setLines(updated)
  }

  const handleAmountOriginalChange = (index: number, value: string) => {
    const updated = [...lines]
    const line = updated[index]
    updated[index] = { ...line, amount_original: value }
    // Recompute CHF
    const chf = computeChf(value, line.exchange_rate)
    if (chf) {
      if (parseFloat(line.debit) >= 0 || !line.credit) {
        updated[index].debit = chf
        updated[index].credit = ''
      } else {
        updated[index].credit = chf
        updated[index].debit = ''
      }
    }
    setLines(updated)
  }

  const handleExchangeRateChange = (index: number, value: string) => {
    const updated = [...lines]
    const line = updated[index]
    updated[index] = { ...line, exchange_rate: value }
    // Recompute CHF
    const chf = computeChf(line.amount_original, value)
    if (chf) {
      if (parseFloat(line.debit) >= 0 || !line.credit) {
        updated[index].debit = chf
        updated[index].credit = ''
      } else {
        updated[index].credit = chf
        updated[index].debit = ''
      }
    }
    setLines(updated)
  }

  const fetchRate = async (currencyCode: string, index: number) => {
    if (!companyForUser || !currencyCode || currencyCode === 'CHF') return
    // Only fetch if no rate is set yet
    if (lines[index]?.exchange_rate) return
    try {
      const res = await ExchangeRateDA.getRate(companyForUser.id, currencyCode, 'CHF')
      if (res.status === 200 && res.data?.rate) {
        setLines(prev => {
          const updated = [...prev]
          if (updated[index] && !updated[index].exchange_rate) {
            updated[index] = { ...updated[index], exchange_rate: String(res.data.rate) }
          }
          return updated
        })
      }
    } catch {
      // Silently ignore — user can type the rate manually
    }
  }

  const blockNegativePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number, field: 'debit' | 'credit') => {
    const pasted = e.clipboardData.getData('text')
    if (pasted.includes('-')) {
      e.preventDefault()
      const abs = Math.abs(parseFloat(pasted.replace(',', '.')))
      const value = isNaN(abs) || abs === 0 ? '' : String(abs)
      if (field === 'debit') handleDebitChange(index, value)
      else handleCreditChange(index, value)
    }
  }

  const hasNegative = lines.some(l => (parseFloat(l.debit) || 0) < 0 || (parseFloat(l.credit) || 0) < 0)

  const handleSave = async (force?: boolean) => {
    if (!companyForUser || !id_fiscal_year || isClosed) return
    setSaving(true)

    const payload = {
      force: force || undefined,
      lines: lines
        .filter(l => (parseFloat(l.debit) || 0) > 0 || (parseFloat(l.credit) || 0) > 0)
        .map(l => {
          const line: any = { id_account: l.id_account, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 }
          if (l.currency_code !== 'CHF' && l.amount_original && l.exchange_rate) {
            line.amount_original = parseFloat(l.amount_original)
            line.exchange_rate = parseFloat(l.exchange_rate)
          }
          return line
        })
    }

    try {
      const response = await OpeningBalanceDA.upsertOpeningBalance(companyForUser.id, id_fiscal_year, payload)
      if (response.status === 200 || response.status === 201) {
        setHasExisting(true)
        GlobalSwal.fire({
          title: lang.labels.successful,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        })
      } else {
        errorResponse = response.data
        const errorCode = errorResponse?.code ?? ''
        const errorMessage = errorResponse?.message ?? ''

        // Conflict: previous fiscal year closed → offer force option
        if (errorCode === 'PREVIOUS_YEAR_CLOSED' || errorMessage.toLowerCase().includes('previous') || errorMessage.toLowerCase().includes('conflit')) {
          GlobalSwal.fire({
            title: lang.labels.forceRepriseWarning,
            text: errorMessage || getError(errorCode, lang.code, errorMessage),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: lang.labels.forceReprise,
            cancelButtonText: lang.labels.cancel,
          }).then((result) => {
            if (result.isConfirmed) {
              handleSave(true)
            }
          })
        } else {
          GlobalSwal.fire({
            title: getError(errorCode, lang.code, errorMessage),
            text: errorMessage || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!companyForUser || !id_fiscal_year || isClosed) return

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmDeleteOpeningBalance,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.delete,
      cancelButtonText: lang.labels.cancel,
    })

    if (!result.isConfirmed) return

    try {
      const response = await OpeningBalanceDA.deleteOpeningBalance(companyForUser.id, id_fiscal_year)
      if (response.status === 200 || response.status === 204) {
        setHasExisting(false)
        setLines(allAccounts.map(a => ({ id_account: a.id, code: a.code, name: a.name, currency_code: a.currency_code, debit: '', credit: '', amount_original: '', exchange_rate: '' })))
        GlobalSwal.fire({
          title: lang.labels.successful,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !companyForUser || !id_fiscal_year) return
    setImporting(true)

    try {
      const response = await OpeningBalanceDA.importOpeningBalance(companyForUser.id, id_fiscal_year, file)
      if (response.status === 200 || response.status === 201) {
        GlobalSwal.fire({
          title: lang.labels.successful,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
        // Reload balance and switch to manual tab
        await loadExistingBalance(companyForUser.id, allAccounts)
        setTab('manual')
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: errorResponse?.message ?? lang.labels.importFailed,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setImporting(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  useEffect(() => {
    if (companyForUser && id_fiscal_year) {
      loadFiscalYear(companyForUser.id)
      loadAccounts(companyForUser.id).then(accounts => {
        if (accounts.length > 0) {
          loadExistingBalance(companyForUser.id, accounts)
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, id_fiscal_year])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.openingBalance}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  const filteredLines = search.length > 0
    ? lines.filter(l => l.code.toLowerCase().includes(search.toLowerCase()) || l.name.toLowerCase().includes(search.toLowerCase()))
    : lines

  // Map filtered back to original indices for editing
  const filteredIndices = search.length > 0
    ? lines.map((l, i) => ({ l, i })).filter(({ l }) => l.code.toLowerCase().includes(search.toLowerCase()) || l.name.toLowerCase().includes(search.toLowerCase())).map(({ i }) => i)
    : lines.map((_, i) => i)

  return (
    <div className="w-100 h-100">
      { companyForUser && allAccounts.length > 0
        ?
        <>
          {/* Header */}
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex flex-column">
                  <h4 className="m-0">{lang.labels.openingBalance}</h4>
                  { fiscalYear &&
                    <span className="text-muted fw-bold fs-7 mt-1">
                      {fiscalYear.name} ({dateFormat(lang.code, fiscalYear.start_date)} – {dateFormat(lang.code, fiscalYear.end_date)})
                    </span>
                  }
                </div>
              </div>
              <div className="card-toolbar">
                { fiscalYear &&
                  <span className={`badge me-3 ${fiscalYear.status === 'open' ? 'badge-light-success' : fiscalYear.status === 'closed' ? 'badge-light-danger' : 'badge-light-warning'}`}>
                    {fiscalYear.status === 'open' ? lang.labels.open : fiscalYear.status === 'closed' ? lang.labels.closed : fiscalYear.status}
                  </span>
                }
                <button className="btn btn-sm btn-light-primary" type="button" onClick={() => navigate(-1)}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-3 me-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9.60001 11H21C21.6 11 22 11.4 22 12C22 12.6 21.6 13 21 13H9.60001V11Z" fill="black"/>
                        <path opacity="0.3" d="M9.6 20V4L2.3 11.3C1.9 11.7 1.9 12.3 2.3 12.7L9.6 20Z" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.back}
                  </div>
                </button>
              </div>
            </div>
          </div>

          { isClosed &&
            <div className="alert alert-warning d-flex align-items-center py-3 mb-5">
              <span className="svg-icon svg-icon-2 svg-icon-warning me-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path opacity="0.3" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="black"/>
                  <path d="M11 7H13V13H11V7ZM11 15H13V17H11V15Z" fill="black"/>
                </svg>
              </span>
              <span className="fw-bold fs-7">{lang.labels.closedFiscalYearReadonly}</span>
            </div>
          }

          {/* Tab selector */}
          <div className="card card-flush mb-5">
            <div className="card-header pt-3 pb-0">
              <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-6 fw-bold">
                <li className="nav-item">
                  <button className={`nav-link text-active-primary pb-3 ${tab === 'manual' ? 'active' : ''}`} type="button" onClick={() => setTab('manual')}>
                    {lang.labels.manualEntry}
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link text-active-primary pb-3 ${tab === 'import' ? 'active' : ''}`} type="button" onClick={() => setTab('import')} disabled={isClosed}>
                    {lang.labels.csvImport}
                  </button>
                </li>
              </ul>
            </div>

            { tab === 'manual'
              ?
              <div className="card-body">
                {/* Search */}
                <div className="d-flex align-items-center mb-4">
                  <input
                    className="form-control form-control-sm w-250px"
                    type="text"
                    placeholder={lang.labels.search}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-sm align-middle table-row-dashed mb-0">
                    <thead className="border-bottom">
                      <tr className="text-muted text-uppercase fw-bolder fs-8">
                        <th style={{ width: 100 }}>{lang.labels.code}</th>
                        <th>{lang.labels.label}</th>
                        <th className="text-center" style={{ width: 80 }}>{lang.labels.currencyColumn}</th>
                        <th className="text-end" style={{ width: 140 }}>{lang.labels.amountInCurrency.replace('{currency}', lang.labels.foreignCurrency)}</th>
                        <th className="text-end" style={{ width: 120 }}>{lang.labels.exchangeRate}</th>
                        <th className="text-end" style={{ width: 150 }}>{lang.labels.debit} (CHF)</th>
                        <th className="text-end" style={{ width: 150 }}>{lang.labels.credit} (CHF)</th>
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      { filteredLines.map((line, fIdx) => {
                        const realIndex = filteredIndices[fIdx]
                        const isFx = line.currency_code !== 'CHF'
                        const inverseRate = line.exchange_rate && parseFloat(line.exchange_rate) > 0 ? (1 / parseFloat(line.exchange_rate)).toFixed(6) : ''
                        return (
                          <tr key={line.id_account}>
                            <td className="fw-semibold fs-7">{line.code}</td>
                            <td className="fs-7">{line.name}</td>
                            <td className="text-center">
                              { isFx
                                ? <span className="badge badge-light-primary">{line.currency_code}</span>
                                : <span className="badge badge-light">CHF</span>
                              }
                            </td>
                            <td style={{ width: 140 }}>
                              { isFx
                                ? <input
                                    className="form-control form-control-sm text-end"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={line.amount_original}
                                    onChange={(e) => handleAmountOriginalChange(realIndex, e.target.value)}
                                    onFocus={() => fetchRate(line.currency_code, realIndex)}
                                    onKeyDown={blockNegativeKeyDown}
                                    disabled={isClosed}
                                    placeholder={line.currency_code}
                                  />
                                : null
                              }
                            </td>
                            <td style={{ width: 120 }}>
                              { isFx
                                ? <div>
                                    <div className="d-flex align-items-center gap-1">
                                      <small className="text-muted text-nowrap">1 CHF =</small>
                                      <input
                                        className="form-control form-control-sm text-end"
                                        style={{ width: 80 }}
                                        type="number"
                                        step="0.000001"
                                        min="0"
                                        value={line.exchange_rate}
                                        onChange={(e) => handleExchangeRateChange(realIndex, e.target.value)}
                                        onFocus={() => fetchRate(line.currency_code, realIndex)}
                                        disabled={isClosed}
                                        placeholder={lang.labels.exchangeRate}
                                      />
                                      <small className="text-muted text-nowrap">{line.currency_code}</small>
                                    </div>
                                    { inverseRate &&
                                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        1 {line.currency_code} ≈ {inverseRate} CHF
                                      </small>
                                    }
                                  </div>
                                : null
                              }
                            </td>
                            <td style={{ width: 150 }}>
                              <input
                                className={`form-control form-control-sm text-end${(parseFloat(line.debit) || 0) < 0 ? ' is-invalid' : ''}${isFx ? ' bg-light-primary' : ''}`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={line.debit}
                                onChange={(e) => handleDebitChange(realIndex, e.target.value)}
                                onKeyDown={blockNegativeKeyDown}
                                onPaste={(e) => blockNegativePaste(e, realIndex, 'debit')}
                                disabled={isClosed}
                                readOnly={isFx && !!line.amount_original && !!line.exchange_rate}
                                placeholder="0.00"
                              />
                              {(parseFloat(line.debit) || 0) < 0 && <div className="invalid-feedback">{lang.labels.amountMustBePositive}</div>}
                            </td>
                            <td style={{ width: 150 }}>
                              <input
                                className={`form-control form-control-sm text-end${(parseFloat(line.credit) || 0) < 0 ? ' is-invalid' : ''}${isFx ? ' bg-light-primary' : ''}`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={line.credit}
                                onChange={(e) => handleCreditChange(realIndex, e.target.value)}
                                onKeyDown={blockNegativeKeyDown}
                                onPaste={(e) => blockNegativePaste(e, realIndex, 'credit')}
                                disabled={isClosed}
                                readOnly={isFx && !!line.amount_original && !!line.exchange_rate}
                                placeholder="0.00"
                              />
                              {(parseFloat(line.credit) || 0) < 0 && <div className="invalid-feedback">{lang.labels.amountMustBePositive}</div>}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="fw-bolder fs-6 border-top">
                        <td colSpan={5} className="text-end">{lang.labels.total}:</td>
                        <td className="text-end">{formatMoney(totalDebit)}</td>
                        <td className="text-end">{formatMoney(totalCredit)}</td>
                      </tr>
                      <tr className="fw-bolder fs-6">
                        <td colSpan={5} className="text-end">{lang.labels.difference}:</td>
                        <td colSpan={2} className={`text-end ${isBalanced ? 'text-success' : 'text-danger'}`}>
                          { isBalanced
                            ? <span className="badge badge-light-success">{lang.labels.balanced}</span>
                            : <span className="badge badge-light-danger">{lang.labels.unbalanced}: {formatMoney(Math.abs(diff))}</span>
                          }
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Action buttons */}
                <div className="d-flex justify-content-end mt-5">
                  { hasExisting && !isClosed &&
                    <button className="btn btn-danger me-3" type="button" onClick={handleDelete}>
                      {lang.labels.deleteOpeningBalance}
                    </button>
                  }
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={!isBalanced || isClosed || saving || hasNegative}
                    onClick={() => handleSave()}
                  >
                    { saving
                      ? <span className="spinner-border spinner-border-sm align-middle me-2"></span>
                      : null
                    }
                    {lang.labels.saveOpeningBalance}
                  </button>
                </div>
              </div>
              :
              <div className="card-body">
                {/* CSV import tab */}
                <div className="mb-5">
                  <p className="fw-bold text-muted fs-7 mb-3">{lang.labels.csvFormatHelp}</p>
                  <div className="bg-light rounded p-4 mb-4">
                    <p className="fw-bold mb-1">Format A: <code>code;debit;credit</code></p>
                    <p className="text-muted fs-7 mb-3">1000;15000;0<br/>2000;0;15000</p>
                    <p className="fw-bold mb-1">Format B: <code>code;solde</code></p>
                    <p className="text-muted fs-7 mb-0">{lang.labels.csvFormatBHelp}</p>
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <input
                    ref={fileInputRef}
                    className="form-control w-auto"
                    type="file"
                    accept=".csv"
                    onChange={handleImport}
                    disabled={isClosed || importing}
                  />
                  { importing &&
                    <span className="spinner-border spinner-border-sm text-primary ms-3"></span>
                  }
                </div>
              </div>
            }
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default OpeningBalancePage;
